export const FISHING_CONTEST_CATEGORIES = Object.freeze(["prize", "rare", "highQuality"]);
export const FISHING_CONTEST_SELECTION_CHANGE_EVENT = "fishing-contest-selection-change";
export const FISHING_CONTEST_VOTING_CLOSE_EVENT = "fishing-contest-voting-close";

function invalid() {
  throw new TypeError("Invalid fishing contest contract.");
}

function exact(value, keys) {
  if (!value || typeof value !== "object" || Array.isArray(value)
    || Object.keys(value).length !== keys.length
    || !keys.every(key => Object.hasOwn(value, key))) invalid();
}

function itemId(value) {
  if (!Number.isSafeInteger(value) || value <= 0) invalid();
  return value;
}

function regionCode(value) {
  // Membership belongs to the existing language dataspace catalog, not another enum.
  if (typeof value !== "string" || !/^[a-z]{2,4}$/.test(value)) invalid();
  return value;
}

function instant(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/.test(value)) invalid();
  const time = Date.parse(value);
  if (!Number.isFinite(time) || new Date(time).toISOString() !== value.replace("Z", ".000Z")) invalid();
  return value;
}

function week(starts, ends) {
  instant(starts);
  instant(ends);
  if (Date.parse(starts) >= Date.parse(ends)) invalid();
}

export function decodeFishingContestCommandV1(value) {
  if (value?.kind === "vote") {
    exact(value, ["schemaVersion", "kind", "region", "weekStartsAt", "category", "itemId"]);
    if (!FISHING_CONTEST_CATEGORIES.includes(value.category)) invalid();
    if (value.itemId !== null) itemId(value.itemId);
  } else if (value?.kind === "downvote") {
    exact(value, ["schemaVersion", "kind", "region", "weekStartsAt", "category", "itemId", "active"]);
    if (!FISHING_CONTEST_CATEGORIES.includes(value.category) || typeof value.active !== "boolean") invalid();
    itemId(value.itemId);
  } else if (value?.kind === "confirm") {
    exact(value, ["schemaVersion", "kind", "region", "weekStartsAt", "itemId"]);
    itemId(value.itemId);
  } else invalid();
  if (value.schemaVersion !== 1) invalid();
  regionCode(value.region);
  instant(value.weekStartsAt);
  return Object.freeze({ ...value });
}

function normalizeSelection(value) {
  if (value.schemaVersion !== 1 || !Array.isArray(value.itemIds) || value.itemIds.length > 3) invalid();
  regionCode(value.region);
  if (value.weekStartsAt === null || value.weekEndsAt === null) {
    if (value.weekStartsAt !== null || value.weekEndsAt !== null || value.itemIds.length !== 0) invalid();
  } else week(value.weekStartsAt, value.weekEndsAt);
  const ids = value.itemIds.map(itemId);
  if (new Set(ids).size !== ids.length) invalid();
  ids.sort((left, right) => left - right);
  return Object.freeze({
    schemaVersion: 1, region: value.region,
    weekStartsAt: value.weekStartsAt, weekEndsAt: value.weekEndsAt,
    itemIds: Object.freeze(ids),
  });
}

export function normalizeFishingContestSelectionProjection(value) {
  exact(value, ["schemaVersion", "region", "weekStartsAt", "weekEndsAt", "itemIds"]);
  return normalizeSelection(value);
}

// Ready empty weeks differ from unavailable or expired selections under NOT/OR.
export function fishingContestSelectionForQuery(value, nowSeconds) {
  if (value == null) return null;
  const selection = normalizeFishingContestSelectionProjection(value);
  if (!Number.isFinite(nowSeconds)) throw new TypeError("Fishing contest resolution requires a finite current time");
  const starts = Date.parse(selection.weekStartsAt) / 1000;
  const ends = Date.parse(selection.weekEndsAt) / 1000;
  return starts <= nowSeconds && nowSeconds < ends ? selection : null;
}
